import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import { Divider, Icon } from '~/components/atomic';
import { IconButtonWrapper } from '~/components/common';
import { Member } from '~/types';
import { validateEmail } from '~/utils';
import { useCommonChatStore, useGroupChatStore } from '~/store';
import * as S from './GroupChatInfo.styled';
import Members from './Members';

export default function GroupChatInfo({
  chatroom_id,
}: {
  chatroom_id?: string;
}) {
  const { showGroupInfo, setShowGroupInfo } = useGroupChatStore((state) => ({
    showGroupInfo: state.showGroupInfo,
    setShowGroupInfo: state.setShowGroupInfo,
  }));
  const {
    chatName,
    participantsNum,
    avatarInfo,
    accessibility,
    chatMembers: members,
    language,
  } = useCommonChatStore((state) => ({
    avatarInfo: state.avatarInfo,
    chatName: state.chatName,
    participantsNum: state.participantsNum,
    accessibility: state.accessibility,
    chatMembers: state.chatMembers,
    language: state.language,
  }));

  const [users, setUsers] = useState<Member[]>([]);
  const [chatAIs, setChatAIs] = useState<Member[]>([]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!members) return;
    const users = members.filter((member) => validateEmail(member.id));
    const chatAIs = members.filter((member) => !validateEmail(member.id));

    setUsers(users);
    setChatAIs(chatAIs);
  }, [members]);

  if (!showGroupInfo) {
    return null;
  }

  return createPortal(
    <>
      <S.GroupChatContainer className='groupChat_info'>
        <S.Background
          className='background'
          onClick={() => setShowGroupInfo(false)}
        />
        <S.Wrapper ref={wrapperRef}>
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding-left: 8px;
            `}
          >
            <S.ChatName>Group info</S.ChatName>
            <IconButtonWrapper onClick={() => setShowGroupInfo(false)}>
              <Icon iconName='Close' />
            </IconButtonWrapper>
          </div>

          <S.InfoWrapper spacing={24}>
            <S.ContentWrapper>
              <S.ContentTitle>Group name</S.ContentTitle>
              <S.ContentDetail>{chatName}</S.ContentDetail>
            </S.ContentWrapper>

            <S.ContentWrapper>
              <S.ContentTitle>Language</S.ContentTitle>
              <S.ContentDetail>{language}</S.ContentDetail>
            </S.ContentWrapper>

            <S.ContentWrapper>
              <S.ContentTitle>Accessibility</S.ContentTitle>
              <S.ContentDetail>{accessibility}</S.ContentDetail>
            </S.ContentWrapper>

            <S.ContentWrapper>
              <S.ContentTitle>
                Participants
                <span>
                  {(participantsNum || 1) +
                    (avatarInfo ? avatarInfo.length : 0)}
                  /{members.length}
                </span>
              </S.ContentTitle>
              <div
                css={css`
                  padding: 0 8px;
                `}
              >
                <div>
                  <S.CharacterTitle>Ai</S.CharacterTitle>
                  {chatAIs.map((member, idx) => (
                    <Members
                      chatroom_id={chatroom_id ? chatroom_id : ''}
                      key={member.id + idx}
                      type={'ai'}
                      member={member}
                    />
                  ))}
                </div>

                <Divider margin={8} />

                <div>
                  <S.CharacterTitle>User</S.CharacterTitle>
                  {users.map((member, idx) => (
                    <Members
                      chatroom_id={chatroom_id ? chatroom_id : ''}
                      key={member.id + idx}
                      type={'user'}
                      member={member}
                    />
                  ))}
                </div>
              </div>
            </S.ContentWrapper>
          </S.InfoWrapper>
        </S.Wrapper>
      </S.GroupChatContainer>
    </>,
    document.body,
  );
}
